import React from 'react'
import { Offer as OfferProps } from '../home/types'
import Diamond from '~/General/Diamond.svg'
import RemoteImage from '@/components/RemoteImage'
import { useSetAtom } from 'jotai'
import { OfferAtomModal, OfferIdAtom } from '@/state/menu'

/** Figma design tokens */
const TOKENS = {
  offBlack: '#18181A',
  white: '#FFFFFF',
  chartreuse: '#BDDC06',
  white60: 'rgba(255, 255, 255, 0.6)',
  accentRed: '#ff2c49',
  accentRedDark: '#6a001a',
  background: 'rgba(0, 0, 0, 0.60)',
} as const

export interface OfferComponentProps extends OfferProps {
  /** 1-based index of current offer (e.g. 1 for first) */
  currentOffer?: number
  /** Total number of offers */
  totalOffers?: number
}

/**
 * Offer - Ad Unit design from Figma (node 90:701)
 * Layout: text left, product image right. Pagination overlay when totalOffers > 1.
 */
function Offer({
  ad_heading,
  logo_url,
  union_message,
  offer_id,
  single,
  ad_subheading,
  currentOffer = 1,
  totalOffers = 1,
}: OfferComponentProps) {
  const setOpenModalOffer = useSetAtom(OfferAtomModal)
  const setOfferId = useSetAtom(OfferIdAtom)
  const imageUrl = logo_url

  const handleOpenModal = () => {
    setOfferId(offer_id)
    setOpenModalOffer(true)
  }

  return (
    <button
      style={{
        display: 'flex',
        width: '100vw',
        maxWidth: '100vw',
        height: 104,
        borderRadius: 12,
        overflow: 'hidden',
        backgroundColor: TOKENS.offBlack,
        border: '1px solid rgba(255, 255, 255, 0.08)',
        textAlign: 'left',
        cursor: 'pointer',
        padding: 0,
        WebkitTapHighlightColor: 'transparent',
      }}
      onClick={handleOpenModal}
    >
      {/* Left: text content */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          padding: '10px 16px',
          backgroundColor: TOKENS.offBlack,
          minWidth: 0,
          justifyContent: 'center',
        }}
      >
        {/* Union message with diamond */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            marginLeft: -5,
          }}
          className="flexa"
        >
          <span style={{ display: 'inline-flex', width: 24, height: 24, flexShrink: 0 }}>
            <Diamond fill={TOKENS.chartreuse} />
          </span>
          <span
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: TOKENS.chartreuse,
              letterSpacing: '-0.01em',
              lineHeight: 1,
              textTransform: 'uppercase',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              minWidth: 0,
            }}
          >
            {union_message}
          </span>
        </div>

        {/* Product name */}
        <p
          style={{
            fontSize: 18,
            color: TOKENS.white,
            margin: 0,
            lineHeight: 1.4,
            alignItems: 'center',
            height: 25,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            minWidth: 0,
            fontFamily: 'NeueRegular',
          }}
        >
          {ad_heading}
        </p>

        {/* Offer description */}
        {ad_subheading && (
          <p
            style={{
              fontSize: 14,
              color: TOKENS.white60,
              lineHeight: 1.1,
              maxWidth: single ? '100%' : 140,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minWidth: 0,
              fontFamily: 'NeueRegular',
            }}
          >
            {ad_subheading}
          </p>
        )}
      </div>

      {/* Right: product image 104×104, rounded right corners */}
      <div
        style={{
          position: 'relative',
          width: 104,
          height: 104,
          flexShrink: 0,
          overflow: 'hidden',
          borderTopRightRadius: 12,
          borderBottomRightRadius: 12,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <RemoteImage image_url={imageUrl} alt_name="Offer" style_classes="block w-full h-full" />
        {/* Pagination overlay - bottom right, when multiple offers */}
        {totalOffers > 1 && (
          <div
            style={{
              position: 'absolute',
              bottom: 5,
              right: 5,
              backgroundColor: TOKENS.background,
              color: TOKENS.white,
              textAlign: 'center',
              fontSize: 12,
              fontStyle: 'normal',
              fontWeight: 500,
              lineHeight: 1,
              textTransform: 'uppercase',
              display: 'inline-flex',
              height: '24px',
              padding: 6,
              justifyContent: 'center',
              alignItems: 'center',
              gap: '10px',
              borderRadius: '6.8px',
            }}
          >
            {String(currentOffer)}/{String(totalOffers)}
          </div>
        )}
      </div>
    </button>
  )
}

export default Offer
